import React, { useState } from 'react';
import {
  IAuditoriaFinanceiraResultado,
  IItemResgateSemIrrf,
  IItemResgateSemIof,
  IItemReceitaMensalAusente,
  IItemConciliacaoConsolidada,
  ILancamentoFinanceira,
} from '../interfaces/IAuditoriaFinanceira';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaFinanceiraResultado;
}

// COR-303: drill-down a nível de lançamento — os achados de Resgate sem IRRF/IOF e Receita
// Mensal Ausente precisam mostrar não só o total consolidado, mas os lançamentos reais.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ lancamentos: ILancamentoFinanceira[] }> = ({ lancamentos }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);

  if (!lancamentos || lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'PENDENTE':
      return 'bi-question-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const statusPior = (items: Array<{ status: string }>): string => {
  if (items.some(x => x.status === 'ERRO')) return 'ERRO';
  if (items.some(x => x.status === 'ALERTA')) return 'ALERTA';
  if (items.some(x => x.status === 'AVISO')) return 'AVISO';
  if (items.some(x => x.status === 'PENDENTE')) return 'PENDENTE';
  return 'CONCILIADO';
};

const contarPorStatus = (items: Array<{ status: string }>) => ({
  erros:     items.filter(x => x.status === 'ERRO').length,
  alertas:   items.filter(x => x.status === 'ALERTA').length,
  avisos:    items.filter(x => x.status === 'AVISO').length,
  pendentes: items.filter(x => x.status === 'PENDENTE').length,
  total:     items.length,
});

const AuditoriaFinanceiraDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const resgatesSemIrrf = resultado.resgatesSemIrrf || [];
  const resgatesSemIof = resultado.resgatesSemIof || [];
  const receitaMensalAusente = resultado.receitaMensalAusente || [];
  const conciliacaoConsolidada = resultado.conciliacaoConsolidada || [];

  const cResgate      = contarPorStatus(resgatesSemIrrf);
  const cIof          = contarPorStatus(resgatesSemIof);
  const cReceita      = contarPorStatus(receitaMensalAusente);
  const cConciliacao  = contarPorStatus(conciliacaoConsolidada);

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria Financeira — Resgate e IRRF sobre Aplicações Financeiras — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% limitada a 1% da receita bruta do período — por inexatidão no SPED (Resgate sem IRRF) + IOF regressivo estimado como devido e não retido (Resgate Antecipado sem IOF). Conciliação Consolidada não gera multa — é alerta de prática contábil.</div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Perda Financeira Estimada</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Rendimento (juros/yield) estimado via taxa SELIC mensal de referência, não reconhecido nos meses em que a aplicação manteve saldo sem receita financeira de competência.</div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultado.creditoTributarioPerdidoTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Crédito Tributário Perdido Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.creditoTributarioPerdidoTotal)}</div>
          <div className='auditoria-kpi-help'>22,5% sobre o rendimento (juros/yield) identificado na ECD para resgates sem IRRF. Zero quando o histórico não segrega o rendimento (Art. 776 RIR/2018).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Ocorrências por Bloco</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>

            <div style={{ border: '1px solid #eef0f4', borderRadius: '6px', padding: '8px 10px', background: '#f9fafb' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '3px' }}>
                Resgate sem IRRF <span style={{ color: '#9ca3af', fontWeight: 400 }}>· por conta ({cResgate.total})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {cResgate.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{cResgate.erros} erro</span>}
                {cResgate.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{cResgate.alertas} alerta</span>}
                {cResgate.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{cResgate.avisos} aviso</span>}
                {cResgate.pendentes > 0 && <span className='auditoria-contador auditoria-contador-pendente'>{cResgate.pendentes} pendente</span>}
                {cResgate.total === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Sem ocorrências</span>}
              </div>
            </div>

            <div style={{ border: '1px solid #eef0f4', borderRadius: '6px', padding: '8px 10px', background: '#f9fafb' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '3px' }}>
                Resgate Antecipado sem IOF <span style={{ color: '#9ca3af', fontWeight: 400 }}>· por conta ({cIof.total})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {cIof.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{cIof.erros} erro</span>}
                {cIof.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{cIof.alertas} alerta</span>}
                {cIof.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{cIof.avisos} aviso</span>}
                {cIof.total === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Sem ocorrências</span>}
              </div>
            </div>

            <div style={{ border: '1px solid #eef0f4', borderRadius: '6px', padding: '8px 10px', background: '#f9fafb' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '3px' }}>
                Receita Mensal Ausente <span style={{ color: '#9ca3af', fontWeight: 400 }}>· por competência ({cReceita.total})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {cReceita.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{cReceita.erros} erro</span>}
                {cReceita.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{cReceita.alertas} alerta</span>}
                {cReceita.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{cReceita.avisos} aviso</span>}
                {cReceita.pendentes > 0 && <span className='auditoria-contador auditoria-contador-pendente'>{cReceita.pendentes} pendente</span>}
                {cReceita.total === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Sem ocorrências</span>}
              </div>
            </div>

            <div style={{ border: '1px solid #eef0f4', borderRadius: '6px', padding: '8px 10px', background: '#f9fafb' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '3px' }}>
                Conciliação Fim de Mês <span style={{ color: '#9ca3af', fontWeight: 400 }}>· por competência ({cConciliacao.total})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {cConciliacao.erros > 0 && <span className='auditoria-contador auditoria-contador-erro'>{cConciliacao.erros} erro</span>}
                {cConciliacao.alertas > 0 && <span className='auditoria-contador auditoria-contador-alerta'>{cConciliacao.alertas} alerta</span>}
                {cConciliacao.avisos > 0 && <span className='auditoria-contador auditoria-contador-aviso'>{cConciliacao.avisos} aviso</span>}
                {cConciliacao.pendentes > 0 && <span className='auditoria-contador auditoria-contador-pendente'>{cConciliacao.pendentes} pendente</span>}
                {cConciliacao.total === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Sem ocorrências</span>}
              </div>
            </div>

          </div>
          <div className='auditoria-kpi-help' style={{ marginTop: '8px' }}>
            Total consolidado: {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s) · {resultado.totalAvisos} aviso(s) · {resultado.totalPendentes} pendente(s)
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada às três análises desta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Resgate sem Vínculo de IRRF:</strong> {resultado.fundamentacaoResgateIrrf}</div>
          <div className='mt-2'><strong>Resgate Antecipado sem IOF:</strong> {resultado.fundamentacaoResgateIof}</div>
          <div className='mt-2'><strong>Receita Financeira de Competência Mensal:</strong> {resultado.fundamentacaoReceitaMensal}</div>
          <div className='mt-2'><strong>Conciliação Consolidada de Fim de Mês:</strong> {resultado.fundamentacaoConciliacao}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Resgate sem Vínculo Obrigatório de IRRF
          {resgatesSemIrrf.length > 0 && (
            <span className={`auditoria-status status-${statusPior(resgatesSemIrrf)}`} style={{ fontSize: '11px' }}>
              {resgatesSemIrrf.length} conta{resgatesSemIrrf.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <p className='text-muted'>Uma linha por conta, somando todas as ocorrências sem IRRF pareado no período. Clique para ver a memória de cálculo completa.</p>

        {resgatesSemIrrf.length === 0 && (
          <div className='text-muted mt-4'>Nenhum resgate de aplicação financeira sem IRRF pareado identificado no período.</div>
        )}

        {resgatesSemIrrf.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Período</th>
                  <th className='text-end'>Ocorrências</th>
                  <th>Produto</th>
                  <th className='text-end'>Valor Total do Resgate</th>
                  <th className='text-end'>Rendimento Identificado</th>
                  <th className='text-end' title='Penalidade por omissão/inexatidão na ECD (5% limitada a 1% da receita bruta), não multa de IRRF; nos pendentes, cálculo de referência sobre o valor bruto'>Multa ECD Potencial</th>
                  <th className='text-end'>Crédito Tributário Perdido</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {resgatesSemIrrf.map((item: IItemResgateSemIrrf, idx) => {
                  const chave = `rs-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>{item.descricaoConta || item.contaContabil}</td>
                        <td>{formatDate(item.dataPrimeiraOcorrencia)} a {formatDate(item.dataUltimaOcorrencia)}</td>
                        <td className='text-end'>{item.quantidadeOcorrencias}</td>
                        <td>
                          <span className={item.produtoFinanceiro === 'DESCONHECIDO' ? 'text-muted' : ''}>
                            {item.produtoFinanceiro}
                          </span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valorResgateTotal)}</td>
                        <td className='text-end'>
                          {item.valorRendimentoTotal > 0
                            ? formatCurrency(item.valorRendimentoTotal)
                            : <span className='text-muted' title='Rendimento não segregado na ECD'>—</span>}
                        </td>
                        <td className='text-end'>{formatCurrency(item.multaIrrf)}</td>
                        <td className='text-end'>{formatCurrency(item.creditoTributarioPerdidoEstimado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={9} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div className='mt-2'>
                                <strong>Pareamento IRRF:</strong>{' '}
                                {item.tipoPareamento === 'JANELA_DATAS' && (
                                  <span className='badge badge-warning ms-1' title={`IRRF encontrado dentro de ±7 dias`}>IRRF no Período (±7 dias)</span>
                                )}
                                {item.tipoPareamento === 'COMPETENCIA_MENSAL' && (
                                  <span className='badge badge-info ms-1' title='IRRF encontrado no mesmo mês, fora da janela de ±7 dias'>IRRF por Competência (mesmo mês)</span>
                                )}
                                {item.tipoPareamento === 'SEM_PAREAMENTO' && item.status === 'ERRO' && (
                                  <span className='badge badge-danger ms-1' title='Nenhum IRRF identificado — rendimento confirmado na ECD'>Sem IRRF Pareado (ERRO)</span>
                                )}
                                {item.tipoPareamento === 'SEM_PAREAMENTO' && item.status === 'PENDENTE' && (
                                  <span className='badge badge-secondary ms-1' title='Rendimento não identificado na ECD — base tributável indeterminada'>Sem IRRF • Rendimento Indeterminado</span>
                                )}
                              </div>
                              <div className='mt-2'>
                                <strong>Produto:</strong> {item.produtoFinanceiro}
                                {item.aliquotaAplicada > 0 && (
                                  <span className='ms-3'>
                                    <strong>Alíquota (tabela regressiva):</strong>{' '}
                                    {(item.aliquotaAplicada * 100).toFixed(1)}%
                                  </span>
                                )}
                                {item.aliquotaAplicada === 0 && item.status === 'PENDENTE' && (
                                  <span className='ms-3 text-muted'>
                                    Alíquota não calculável — rendimento não segregado na ECD (base indeterminada)
                                  </span>
                                )}
                                {item.aliquotaAplicada === 0 && item.status !== 'PENDENTE' && (
                                  <span className='ms-3 text-muted'>
                                    Alíquota pendente de validação
                                  </span>
                                )}
                              </div>
                              {item.valorRendimentoTotal > 0 && (
                                <div className='mt-2'>
                                  <strong>Breakdown:</strong>{' '}
                                  Principal resgatado: {formatCurrency(item.valorResgateTotal)} &nbsp;|&nbsp;
                                  Rendimento (base IRRF): {formatCurrency(item.valorRendimentoTotal)} &nbsp;|&nbsp;
                                  Crédito estimado ({(item.aliquotaAplicada * 100).toFixed(1)}% × rendimento):{' '}
                                  {formatCurrency(item.creditoTributarioPerdidoEstimado)}
                                </div>
                              )}
                              {item.valorRendimentoTotal === 0 && item.status === 'PENDENTE' && (
                                <div className='mt-2 text-warning'>
                                  <i className='bi bi-question-circle me-1' />
                                  Rendimento não identificado separadamente na ECD — crédito tributário não calculado;
                                  a Multa ECD (3%) é cálculo de referência sobre o valor bruto, com base e exigibilidade a confirmar.
                                  Verificar histórico individualmente para confirmar se IRRF era devido e qual o valor do rendimento.
                                </div>
                              )}
                              {item.valorRendimentoTotal === 0 && item.status !== 'PENDENTE' && (
                                <div className='mt-2 text-muted'>
                                  Rendimento não identificado separadamente na ECD — resgate consolidado.
                                  Verifique o histórico individualmente para confirmar se IRRF era devido.
                                </div>
                              )}
                              <div className='mt-2'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='auditoria-card'>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Resgate Antecipado sem Retenção de IOF
          {resgatesSemIof.length > 0 && (
            <span className={`auditoria-status status-${statusPior(resgatesSemIof)}`} style={{ fontSize: '11px' }}>
              {resgatesSemIof.length} conta{resgatesSemIof.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <p className='text-muted'>
          Resgates realizados em até 29 dias da aplicação (resgate antecipado), com rendimento identificado na ECD, sem IOF regressivo pareado.
          Só entram aqui casos com prazo e rendimento confirmados — sem essas duas evidências o achado é omitido, não estimado.
        </p>

        {resgatesSemIof.length === 0 && (
          <div className='text-muted mt-4'>Nenhum resgate antecipado sem IOF pareado identificado no período.</div>
        )}

        {resgatesSemIof.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Período</th>
                  <th className='text-end'>Ocorrências</th>
                  <th>Produto</th>
                  <th className='text-end'>Rendimento (base do IOF)</th>
                  <th className='text-end'>IOF Estimado Devido</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {resgatesSemIof.map((item: IItemResgateSemIof, idx) => {
                  const chave = `io-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>{item.descricaoConta || item.contaContabil}</td>
                        <td>{formatDate(item.dataPrimeiraOcorrencia)} a {formatDate(item.dataUltimaOcorrencia)}</td>
                        <td className='text-end'>{item.quantidadeOcorrencias}</td>
                        <td>
                          <span className={item.produtoFinanceiro === 'DESCONHECIDO' ? 'text-muted' : ''}>
                            {item.produtoFinanceiro}
                          </span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valorRendimentoTotal)}</td>
                        <td className='text-end'>{formatCurrency(item.iofDevidoEstimado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div className='mt-2'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='auditoria-card'>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Receita Financeira de Competência Mensal Ausente
          {receitaMensalAusente.length > 0 && (
            <span className={`auditoria-status status-${statusPior(receitaMensalAusente)}`} style={{ fontSize: '11px' }}>
              {receitaMensalAusente.length} competência{receitaMensalAusente.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <p className='text-muted'>Meses em que a aplicação financeira manteve saldo sem reconhecimento de receita financeira no mês — o rendimento estimado (taxa SELIC de referência) representa perda financeira, não risco fiscal.</p>

        {receitaMensalAusente.length === 0 && (
          <div className='text-muted mt-4'>Nenhum mês com saldo de aplicação sem receita financeira de competência identificado no período.</div>
        )}

        {receitaMensalAusente.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Saldo no Mês</th>
                  <th className='text-end'>Rendimento Não Contabilizado (estimado)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {receitaMensalAusente.map((item: IItemReceitaMensalAusente, idx) => {
                  const chave = `rm-${item.empresaId}-${item.contaContabil}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>{item.descricaoConta || item.contaContabil}</td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(item.saldoAplicacaoNoMes)}</td>
                        <td className='text-end'>{formatCurrency(item.rendimentoNaoContabilizadoEstimado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados lancamentos={item.lancamentosRelacionados} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='auditoria-card'>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Conciliação Consolidada de Fim de Mês
          {conciliacaoConsolidada.length > 0 && (
            <span className={`auditoria-status status-${statusPior(conciliacaoConsolidada)}`} style={{ fontSize: '11px' }}>
              {conciliacaoConsolidada.length} competência{conciliacaoConsolidada.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <p className='text-muted'>
          Receita financeira reconhecida na competência, mas com todos os lançamentos concentrados nos <strong>últimos 2 dias do mês</strong> —
          indício de ausência de conciliação periódica (ITG 2000). A receita foi reconhecida no mês correto; não há omissão fiscal nem multa automática.
          Recomenda-se revisar a periodicidade de reconhecimento ao longo do mês.
        </p>

        {conciliacaoConsolidada.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento consolidado de fim de mês identificado no período.</div>
        )}

        {conciliacaoConsolidada.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Competência</th>
                  <th className='text-end'>Valor Reconhecido</th>
                  <th>Último Lançamento</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {conciliacaoConsolidada.map((item: IItemConciliacaoConsolidada, idx) => {
                  const chave = `cc-${item.empresaId}-${item.contaContabil}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>{item.descricaoConta || item.contaContabil}</td>
                        <td>{item.competencia}</td>
                        <td className='text-end'>{formatCurrency(item.valorConsolidado)}</td>
                        <td>{formatDate(item.dataLancamentoUnico)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div className='mt-2'>
                                <strong>Critério de consolidação:</strong>{' '}
                                <span className='badge badge-warning ms-1'>Últimos 2 dias do mês</span>
                                <span className='text-muted ms-2 small'>Todos os lançamentos do mês caíram nesse intervalo.</span>
                              </div>
                              <div className='mt-2'><strong>Detalhamento:</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaFinanceiraDetalhe;
